/** Game configuration — unit archetypes + costs + limits.
 *
 *  The archetype catalog (stats, cost, ruleset, sprite, point budget, max
 *  army size) is loaded directly from the server's authoritative file:
 *  `server-rs/archetypes.json`. That file is THE single source of truth —
 *  the server reads it via `include_str!` (see `server-rs/crates/domain/src/config.rs`)
 *  and the client reads it via this import (esbuild bundles JSON natively).
 *
 *  To change unit stats, costs, sprite assignments, ruleset assignments, the
 *  army point budget, or the max army size, edit ONLY `server-rs/archetypes.json`
 *  and rebuild both the server and the client.
 */

// `resolveJsonModule` is enabled in tsconfig.json. esbuild bundles JSON natively.
import catalog from "../../server-rs/archetypes.json";

interface CultureShape {
    display_name: string;
    default_color?: string;
    banner?: string;
    banned_units?: string[];
    unit_caps?: Record<string, number>;
}

interface CatalogShape {
    meta: {
        army_budget: number;
        max_army_size: number;
        default_ruleset: string;
    };
    archetypes: Record<string, any>;
    cultures?: Record<string, Record<string, CultureShape>>;
    custom_units?: Record<string, any>;
}

const typedCatalog = catalog as unknown as CatalogShape;

export const ARMY_POINT_BUDGET: number = typedCatalog.meta.army_budget;
export const MAX_ARMY_SIZE: number = typedCatalog.meta.max_army_size;

// Wide `any` value type because archetypes.json carries fields that vary by
// unit (ranged-only fields, projectile metadata, etc.). The schema is defined
// authoritatively on the server in `crates/domain/src/config.rs::ArchetypeStats`.
export const ARCHETYPES: Record<string, any> = typedCatalog.archetypes;
export const CUSTOM_UNITS: Record<string, any> = typedCatalog.custom_units ?? { rulesets: {} };

export type BattleRuleset = "classical" | "napoleonic";

export const DEFAULT_RULESET: BattleRuleset =
    (typedCatalog.meta.default_ruleset as BattleRuleset) || "classical";

export function archetypeRuleset(archetype: string): BattleRuleset {
    const r = ARCHETYPES[archetype]?.ruleset;
    return r === "napoleonic" ? "napoleonic" : "classical";
}

// ── Cultures ────────────────────────────────────────────────────────────────

export type CultureConfig = Required<Pick<CultureShape, "display_name">> & {
    default_color: string;
    banner: string;
    banned_units: string[];
    unit_caps: Record<string, number>;
};

const RAW_CULTURES = (typedCatalog.cultures ?? {}) as Record<
    string,
    Record<string, CultureShape>
>;

export const CULTURES: Record<string, Record<string, CultureConfig>> = Object.fromEntries(
    Object.entries(RAW_CULTURES).map(([ruleset, map]) => [
        ruleset,
        Object.fromEntries(
            Object.entries(map).map(([id, def]) => [
                id,
                {
                    display_name: def.display_name,
                    default_color: def.default_color ?? "#888888",
                    banner: def.banner ?? "",
                    banned_units: def.banned_units ?? [],
                    unit_caps: def.unit_caps ?? {},
                },
            ]),
        ),
    ]),
);

export function archetypeCulture(archetype: string): string | null {
    const c = ARCHETYPES[archetype]?.culture;
    return typeof c === "string" && c.length > 0 ? c : null;
}

export function cultureKeysForRuleset(ruleset: string): string[] {
    return Object.keys(CULTURES[ruleset] ?? {}).sort();
}

export function defaultCulture(ruleset: string): string {
    return cultureKeysForRuleset(ruleset)[0] ?? "";
}

export function cultureDefinition(ruleset: string, culture: string): CultureConfig | null {
    return CULTURES[ruleset]?.[culture] ?? null;
}

export function cultureAllowsUnit(
    ruleset: string,
    culture: string,
    archetype: string,
): boolean {
    // Restrictions are driven purely by each culture's banned_units list.
    // An archetype's own `culture` tag is just an informational default.
    const def = cultureDefinition(ruleset, culture);
    if (def && def.banned_units.includes(archetype)) return false;
    return true;
}

export function cultureUnitCap(
    ruleset: string,
    culture: string,
    archetype: string,
): number | null {
    const def = cultureDefinition(ruleset, culture);
    if (!def) return null;
    const cap = def.unit_caps[archetype];
    return typeof cap === "number" ? cap : null;
}
